'use client';

import { useEffect, useState } from 'react';
import { orchestratorApi } from '@/app/services/orchestrator-api.service';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Alert,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Nudge, not a gate (password-management-plan.md, D2). Shown once per page load when the JWT's
// mustChangePassword claim is true; the user can close it and keep using the app — it reappears
// next time they log in fresh (the claim doesn't change until they actually set a new password),
// but never blocks navigation. An earlier version force-redirected on every pathname change;
// that was too aggressive for something that isn't a hard security boundary, so it's a dismissible
// modal instead.
export default function MustChangePasswordModal() {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setOpen(orchestratorApi.getMustChangePassword());
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    setLoading(true);
    try {
      const { token } = await orchestratorApi.changePassword(currentPassword, newPassword);
      orchestratorApi.setToken(token);
      setOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Set a New Password
        <IconButton size="small" onClick={() => setOpen(false)} aria-label="Dismiss">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your password was set by an admin. You can change it now, or dismiss this and do it
            later — it'll show up again next time you log in until you do.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Current Password"
            type="password"
            fullWidth
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            sx={{ mb: 2 }}
          />
          <TextField
            label="New Password"
            type="password"
            fullWidth
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            helperText="Min 12 chars, upper + lower case, a digit, a special character"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Confirm New Password"
            type="password"
            fullWidth
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Remind Me Later</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Set Password'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
